import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import dayjs from 'dayjs';
import {Message} from '../../generic/classes/message';
import {ApplicationService} from '../../generic/service/application.service';
import {FilterService} from '../../generic/service/filter.service';
import {DatepickerComponent} from '../../datepicker/datepicker.component';

interface RangeFilter {
	range: number[];
	filter: string;
}

@Component({
	selector: 'filter-order-history',
	styleUrls: ['./filter.component.scss', './featuregroup.component.scss'],
	templateUrl: './filter.order.history.html',
	standalone: true,
	imports: [DatepickerComponent],
})
export class FilterOrderHistoryComponent implements OnInit, OnDestroy {
	private filterService = inject(FilterService);
	private applicationService = inject(ApplicationService);

	// Work around i18n limitations
	// @see https://github.com/angular/angular/issues/11405#issuecomment-280850945
	@Input()
	public message: string;

	dateFrom: Date | null;
	dateTo: Date | null;

	today: Date | null = new Date();
	lastTwoWeeksRange: number[] = [
		FilterOrderHistoryComponent.getDateForTwoWeeksAgo().getTime(),
		(<Date>FilterOrderHistoryComponent.normalizeDate(new Date())).getTime()
	];
	lastMonthRange: number[] = [
		FilterOrderHistoryComponent.getDateForOneMonthAgo().getTime(),
		(<Date>FilterOrderHistoryComponent.normalizeDate(new Date())).getTime()
	];

	lastTwoWeeksChecked = false;
	lastMonthChecked = false;
	samePeriodLastYearChecked = false;

	private static normalizeDate(date: Date | null): Date | null {
		if (date) {
			date.setHours(0, 0, 0, 0);
		}
		return date;
	}

	private static getDateForTwoWeeksAgo(): Date {
		return <Date>FilterOrderHistoryComponent.normalizeDate(dayjs().subtract(2, 'week').startOf('day').toDate());
	}

	private static getDateForOneMonthAgo(): Date {
		return <Date>FilterOrderHistoryComponent.normalizeDate(dayjs().subtract(1, 'month').startOf('day').toDate());
	}

	ngOnInit(): void {
		this.initializeMessage();
		this.initializeDates();
		this.setPeriod(false);
	}

	ngOnDestroy(): void {
		this.dateFrom = null;
		this.dateTo = null;
		this.today = null;
		this.applicationService.message = null;
	}

	/**
	 * Set the from date input field by selecting a date from a 'calendar'
	 * When choosing a date from the 'calendar' dropdown a 'Date' instance will be
	 * set.
	 *
	 * @param {Date} value
	 */
	setDateFrom(value: Date): void {
		this.dateFrom = value;
		this.setAndCheckPeriod();
	}

	/**
	 * Set the to date input field by selecting a date from a 'calendar'
	 * When choosing a date from the 'calendar' dropdown a 'Date' instance will be
	 * set.
	 *
	 * @param {Date} value
	 */
	setDateTo(value: Date): void {
		this.dateTo = value;
		this.setAndCheckPeriod();
	}

	setAndCheckPeriod() {
		this.setPeriod(true);
		this.checkSelectedRange();
	}

	setLastTwoWeeks(): void {
		this.setDateTo(this.today as Date);
		this.setDateFrom(FilterOrderHistoryComponent.getDateForTwoWeeksAgo());
		this.lastTwoWeeksChecked = true;
	}

	setLastMonth(): void {
		this.setDateTo(this.today as Date);
		this.setDateFrom(FilterOrderHistoryComponent.getDateForOneMonthAgo());
		this.lastMonthChecked = true;
	}

	setSamePeriodLastYear(): void {
		// Check if not already selected
		if (!this.samePeriodLastYearChecked) {
			const toDateLastYear = dayjs(this.dateTo)
				.subtract(1, 'year')
				.toDate();
			const fromDateLastYear = dayjs(this.dateFrom)
				.subtract(1, 'year')
				.toDate();

			this.setDateTo(toDateLastYear);
			this.setDateFrom(fromDateLastYear);

			this.samePeriodLastYearChecked = true;
		}
	}

	getInputText(date: Date): string {
		return dayjs(date).format('DD-MM-YYYY');
	}

	setOrderDateFilters(selection: string): void {
		this.resetOrderDateFilters();
		switch (selection) {
			case 'lastTwoWeeks':
				this.lastTwoWeeksChecked = true;
				break;
			case 'lastMonth':
				this.lastMonthChecked = true;
				break;
			case 'samePeriodLastYear':
				this.samePeriodLastYearChecked = true;
				break;
			default:
				this.lastTwoWeeksChecked = true;
				break;
		}
	}

	private initializeMessage(): void {
		this.applicationService.message = new Message(
			this.message,
			'info',
			true,
			false,
		);
	}

	private initializeDates(): void {
		const filterState = this.filterService.getFilterState();

		this.dateFrom = FilterOrderHistoryComponent.normalizeDate(filterState.orderHistoryDateFrom ?? FilterOrderHistoryComponent.getDateForTwoWeeksAgo());
		this.dateTo = FilterOrderHistoryComponent.normalizeDate(filterState.orderHistoryDateTo ?? new Date());
		this.checkSelectedRange();
	}

	private setPeriod(notifyFilterStateChange: boolean): void {
		this.resetOrderDateFilters();
		const filterState = this.filterService.getFilterState();

		if (
			this.dateFrom !== filterState.orderHistoryDateFrom ||
			this.dateTo !== filterState.orderHistoryDateTo
		) {
			this.filterService.setOrderHistoryPeriod(
				notifyFilterStateChange,
				this.dateFrom as Date,
				this.dateTo as Date,
			);
		}
	}

	private checkSelectedRange(): void {
		if (this.dateFrom && this.dateTo) {
			const selectedRange: number[] = [
				this.dateFrom.getTime(),
				this.dateTo.getTime(),
			];
			const ranges: RangeFilter[] = [
				{range: this.lastTwoWeeksRange, filter: 'lastTwoWeeks'},
				{range: this.lastMonthRange, filter: 'lastMonth'},
			];

			for (const {range, filter} of ranges) {
				if (this.compareRanges(selectedRange, range)) {
					this.setOrderDateFilters(filter);
					return;
				}
			}
		}

		this.resetOrderDateFilters();
	}

	private compareRanges(
		selectedRange: number[],
		predefinedRange: number[],
	): boolean {
		if (selectedRange.length !== predefinedRange.length) {
			return false;
		}
		for (let i = 0; i < selectedRange.length; i += 1) {
			if (selectedRange[i] !== predefinedRange[i]) {
				return false;
			}
		}
		return true;
	}

	private resetOrderDateFilters(): void {
		this.lastTwoWeeksChecked = false;
		this.lastMonthChecked = false;
		this.samePeriodLastYearChecked = false;
	}
}
